"use client"

import { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { ArrowRight, ArrowLeft, Zap, CheckCircle, Bot, Lightbulb, Search, Rocket, Mail, Upload, Shield, User } from "lucide-react"
import { EmailModal } from "./email-modal"
import { ShowcaseCard } from "./showcase-card"
import { useAigency } from "@/components/aigency-modal/AigencyContext"
import { TeamSection } from "@/components/team-section"
import { DualCTA } from "@/components/dual-cta"
import { FileUploader } from "./file-uploader"
import { useMutation } from "convex/react"
import { api } from "@/convex/_generated/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { ShowcaseProject, Example } from "@/types/showcase"

// Type definitions
type ExamplesData = {
  product: Example[]
  action: Example[]
  customer: Example[]
}

interface MultiStepIdeaFormProps {
  initialExamples?: ExamplesData
  showcaseProjects?: ShowcaseProject[]
}

interface UploadedFile {
  url: string
  filename: string
  size: number
  contentType: string
  fileId?: string
}

// Hardcoded intern data (same as in intern-member.tsx)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const INTERN_CANDIDATES = [
  {
    _id: "k578w6eb8xawpsmjbfbfvmm8497p650g",
    vapiId: "21cced8b-3e1e-454a-b0e6-826bcfc6203a",
    name: "Intern Candidate #1",
    role: "AI Intern",
    firstMessage: "Hello! I'm excited to discuss how I can contribute to TRPC.",
    instructions: "You open your eyes and are in the middle of dimly lit room..."
  },
  {
    _id: "k578tsanqf0d7k6dpvza5qerc57p6cth",
    vapiId: "153c3dac-d80b-4cc7-8d0f-39ba7e7051c7",
    name: "Hannah",
    role: "Customer Engagement Intern",
    firstMessage: "Hi, thanks for calling TRPC. This is Hannah. Happy to walk through your idea or if you have any question about TRPC.",
    instructions: "You are Hannah, the Inbound Customer Engagement Representative..."
  },
  {
    _id: "intern_candidate_3",
    vapiId: "886caf53-7df2-4eb8-8899-1ac36eab0313",
    name: "Intern Candidate #3",
    role: "AI Intern",
    firstMessage: "Greetings! I'm ready to showcase my skills and learn from the TRPC team.",
    instructions: "An eager AI intern ready to prove their worth..."
  }
]

export function MultiStepIdeaForm({ showcaseProjects = [] }: MultiStepIdeaFormProps) {
  const createSubmission = useMutation(api.submissions.create)
  const { openConversation } = useAigency()
  
  // Glassmorphism style object for reuse
  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.21)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '20px',
    border: '0.25px solid rgba(255, 255, 255, 0.1)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1),
      inset 0 0 42px 21px rgba(255, 255, 255, 0.05)
    `
  }

  const GlassmorphismHighlights = () => (
    <>
      {/* Top highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
        }}
      />
      {/* Left highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '1px',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
        }}
      />
    </>
  )

  const router = useRouter()
  const [currentStep, setCurrentStep] = useState(1)
  const [mounted, setMounted] = useState(false)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)

  // State for input values (Step 1)
  const [product, setProduct] = useState("")
  const [action, setAction] = useState("")
  const [customer, setCustomer] = useState("")

  // State for active field and suggestions

  // State for Step 2 (Email page)
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState("")
  const [details, setDetails] = useState("")
  const [agreed, setAgreed] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isStealthMode] = useState(false)
  const fileUploaderRef = useRef<{ resetUploader: () => void }>(null)

  // Check if form is complete
  const isFormComplete = product && action && customer

  // Initialize component
  useEffect(() => {
    setProduct("")
    setAction("")
    setCustomer("")
    localStorage.removeItem("formData")
    setMounted(true)

    const savedFormData = localStorage.getItem("formData")
    if (savedFormData) {
      const { product: savedProduct, action: savedAction, customer: savedCustomer } = JSON.parse(savedFormData)
      setProduct(savedProduct || "")
      setAction(savedAction || "")
      setCustomer(savedCustomer || "")
    }
  }, [])

  // Save form data when complete
  useEffect(() => {
    if (isFormComplete) {
      localStorage.setItem("formData", JSON.stringify({ product, action, customer }))
    }
  }, [isFormComplete, product, action, customer])

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const formArea = document.getElementById("form-area")
      const suggestionsArea = document.getElementById("suggestions-area")

      if (formArea && !formArea.contains(event.target as Node) && 
          suggestionsArea && !suggestionsArea.contains(event.target as Node)) {
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])



  // Email page functions
  const handleFileUploadComplete = (fileData: UploadedFile) => {
    setUploadedFiles((prev) => [...prev, fileData])
    if (fileUploaderRef.current && fileUploaderRef.current.resetUploader) {
      fileUploaderRef.current.resetUploader()
    }
  }

  const handleFileUploadError = (errorMsg: string) => {
    setError(errorMsg)
  }

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError("")

    // Basic email validation
    if (!email || !email.includes("@") || !email.includes(".")) {
      setError("Please enter a valid email address")
      return
    }

    // Check if user agreed to privacy policy
    if (!agreed) {
      setError("Please agree to our privacy policy")
      return
    }

    setIsSubmitting(true)

    try {
      // Save submission to database
      // Extract file IDs from uploadedFiles
      const fileIds = uploadedFiles
        .filter(file => file.fileId)
        .map(file => file.fileId as string)
      
      const result = await createSubmission({
        email,
        product: isStealthMode ? "PRODUCT" : product,
        action: isStealthMode ? "ACTION" : action,
        customer: isStealthMode ? "CUSTOMER" : customer,
        details,
        agreed,
        fileIds: fileIds.length > 0 ? fileIds : undefined,
      })

      if (result.success) {
        setIsSubmitted(true)
        // Clear form data and stealth mode from localStorage after successful submission
        localStorage.removeItem("formData")
        localStorage.removeItem("stealthMode")
      } else {
        setError("Something went wrong. Please try again.")
      }
    } catch {
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!mounted) {
    return null
  }

  if (isSubmitted) {
    return (
      <div className="flex items-center justify-center p-4 py-24">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Thank You!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-muted-foreground">
              We&apos;ve received your submission and will get back to you within 24 hours.
            </p>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-semibold mb-2">What happens next?</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• We&apos;ll review your project details</p>
                  <p>• Schedule a discovery call to discuss your vision</p>
                  <p>• Provide a detailed proposal and timeline</p>
                  <p>• Begin building your prototype</p>
                </div>
              </div>
            </div>
            <Button onClick={() => {
              setCurrentStep(1)
              setIsSubmitted(false)
              setProduct("")
              setAction("")
              setCustomer("")
              setEmail("")
              setDetails("")
              setAgreed(false)
              setUploadedFiles([])
            }} className="w-full">
              Submit Another Idea
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {currentStep === 1 ? (
        <>
          {/* Step 1: Original Three Input Form */}
          <section className="relative min-h-[90vh] flex items-center justify-center px-4">
            <div className="max-w-6xl mx-auto text-center space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-tight relative">
                  <span className="text-primary">
                    Stop{" "}
                    <span className="relative">
                      {/* Playful "over" annotation above the gap between Stop and thinking */}
                      <div className="absolute -top-8 md:-top-16 -left-2 md:-left-3 lg:-left-4 transform -translate-x-1/2 flex flex-col items-center whitespace-nowrap">
                        <span className="text-3xl md:text-5xl lg:text-6xl text-muted-foreground/80 font-handwriting rotate-[-8deg]">
                          Over
                        </span>
                        <div className="text-muted-foreground/60 text-2xl md:text-3xl lg:text-4xl font-handwriting rotate-[3deg] -mt-2">
                          ˅
                        </div>
                      </div>
                    </span>thinking.
                  </span>
                  <br />
                  Start building.
                </h1>
                <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                  We help founders and innovation teams build fast, validate early, and launch smarter in as little as 2 weeks.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="pt-8"
              >
                {/* Connect heading */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-semibold text-white">
                    Connect With Us
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row gap-8 justify-center items-start">
                  <div className="flex flex-col items-center gap-3">
                    <User className="h-6 w-6 text-muted-foreground/80" />
                    <Button 
                      size="lg" 
                      className="px-6 py-4 h-auto min-w-[240px]" 
                      onClick={() => {
                        openConversation({
                          _id: `team-Khurram Ahmad`,
                          name: "Khurram Ahmad",
                          displayName: "Khurram",
                          role: "Co-Founder",
                          calendlyUrl: "trpc-book/30min",
                          linkedinUrl: "https://www.linkedin.com/in/khurramahmad",
                          imageSrc: "/images/khurram.png"
                        }, 'human')
                      }}
                    >
                      <span className="text-lg font-semibold">Book a Discovery Call</span>
                    </Button>
                  </div>
                  {/* HIDDEN — AI intern CTA + "follow along with the journey" link. Restore when re-enabling the AI intern flow.
                  <div className="flex flex-col items-center gap-3">
                    <Bot className="h-6 w-6 text-muted-foreground/80" />
                    <Button
                      size="lg"
                      variant="outline"
                      className="px-6 py-4 h-auto relative min-w-[240px]"
                      style={{
                        boxShadow: '0 0 20px rgba(124, 58, 237, 0.5), 0 0 40px rgba(59, 130, 246, 0.3)',
                        border: '1px solid rgba(124, 58, 237, 0.6)'
                      }}
                      onClick={() => {
                        const randomIntern = INTERN_CANDIDATES[Math.floor(Math.random() * INTERN_CANDIDATES.length)]
                        openConversation({
                          _id: randomIntern._id,
                          vapiId: randomIntern.vapiId,
                          name: randomIntern.name,
                          role: randomIntern.role,
                          firstMessage: randomIntern.firstMessage,
                          instructions: randomIntern.instructions
                        }, 'assistant')
                      }}
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-lg font-semibold">Chat with Our Intern</span>
                        <span className="text-sm text-foreground/70 mt-1">(AI and maybe future CEO)</span>
                      </div>
                    </Button>
                    <Link
                      href="/blog"
                      className="text-xs text-muted-foreground/70 hover:text-muted-foreground/90 transition-colors underline"
                    >
                      follow along with the journey
                    </Link>
                  </div>
                  */}
                </div>
              </motion.div>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-24 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  What we do
                </h2>
                <p className="text-xl text-muted-foreground">
                  We help founders and innovation teams turn ideas into reality
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Rapid Prototyping */}
                <Card className="p-8 !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                  <GlassmorphismHighlights />
                  <CardContent className="space-y-6">
                    <div className="flex items-center gap-3">
                      <Zap className="h-8 w-8 text-foreground" />
                      <h3 className="text-2xl font-bold">Rapid Prototyping</h3>
                    </div>
                    <p className="text-muted-foreground">
                      MVPs, mockups and working demos to validate fast
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Interactive prototypes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">User testing ready</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Investor pitch demos</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Market validation</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Technical feasibility</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Workflows & Agents */}
                <Card className="p-8 !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                  <GlassmorphismHighlights />
                  <CardContent className="space-y-6">
                    <div className="flex items-center gap-3">
                      <Bot className="h-8 w-8 text-foreground" />
                      <h3 className="text-2xl font-bold">AI Workflows & Agents</h3>
                    </div>
                    <p className="text-muted-foreground">
                      Automations that actually work in your business
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Custom AI agents</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Workflow automation</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Data processing</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Task automation</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Integration setup</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Innovation Support */}
                <Card className="p-8 !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                  <GlassmorphismHighlights />
                  <CardContent className="space-y-6">
                    <div className="flex items-center gap-3">
                      <Lightbulb className="h-8 w-8 text-foreground" />
                      <h3 className="text-2xl font-bold">Innovation Support</h3>
                    </div>
                    <p className="text-muted-foreground">
                      Facilitation, workshops and product discovery
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Design thinking workshops</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Product discovery sessions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Innovation strategy</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">User research</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-foreground" />
                        <span className="text-sm">Stakeholder alignment</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* About Section - Meet the Team */}
          <section className="py-24 px-4 bg-muted/20">
            <TeamSection />
          </section>

          {/* How It Works */}
          <section className="py-24 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">How it works</h2>
                <p className="text-xl text-muted-foreground">
                  From idea to reality in three focused steps
                </p>
              </div>

              <div className="relative max-w-4xl mx-auto">
                {/* Timeline container - Hidden on mobile */}
                <div className="relative mb-12 hidden md:block">
                  {/* Timeline dots - positioned to match grid columns */}
                  <div className="grid md:grid-cols-3 gap-8 relative">
                    {[
                      { 
                        icon: Search, 
                        title: "Discovery & Clarity", 
                        desc: "We help shape the right problem and solution.",
                        number: "01"
                      },
                      { 
                        icon: Zap, 
                        title: "Prototype Sprint", 
                        desc: "We design and build in short iterations.",
                        number: "02"
                      },
                      { 
                        icon: Rocket, 
                        title: "Handoff or Extend", 
                        desc: "Get a clean handoff or let us support further development.",
                        number: "03"
                      }
                    ].map((_, index) => (
                      <div key={index} className="flex justify-center relative">
                        {/* Timeline dot */}
                        <div className="w-4 h-4 bg-primary rounded-full border-2 border-background relative z-10"></div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Horizontal timeline line - positioned to go through center of dots */}
                  <div className="absolute top-2 left-0 right-0 h-0.5 bg-border"></div>
                </div>
                
                {/* Content boxes */}
                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    { 
                      icon: Search, 
                      title: "Discovery & Clarity", 
                      desc: "We help shape the right problem and solution.",
                      number: "01"
                    },
                    { 
                      icon: Zap, 
                      title: "Prototype Sprint", 
                      desc: "We design and build in short iterations.",
                      number: "02"
                    },
                    { 
                      icon: Rocket, 
                      title: "Handoff or Extend", 
                      desc: "Get a clean handoff or let us support further development.",
                      number: "03"
                    }
                  ].map((step, index) => (
                    <Card key={index} className="p-6 !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                      <GlassmorphismHighlights />
                      <CardContent className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <step.icon className="h-6 w-6 text-primary" />
                          </div>
                        </div>
                        <h3 className="text-xl font-bold">{step.title}</h3>
                        <p className="text-muted-foreground">{step.desc}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Portfolio Section - Now with Real Showcase Content */}
          <section className="py-24 px-4 bg-muted/20">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">What we&apos;ve done</h2>
                <p className="text-xl text-muted-foreground">
                  A showcase of recent projects we&apos;ve brought to life
                </p>
              </div>

              {showcaseProjects.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {showcaseProjects.slice(0, 6).map((project, index) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <ShowcaseCard project={project} />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-8">
                  {[1, 2, 3, 4, 5, 6].map((item) => (
                    <Card key={item} className="group hover:shadow-lg transition-shadow !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                      <GlassmorphismHighlights />
                      <CardContent className="p-0">
                        <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/10 rounded-t-lg flex items-center justify-center">
                          <span className="text-muted-foreground">Project {item}</span>
                        </div>
                        <div className="p-6">
                          <h3 className="font-semibold mb-2">Project Title {item}</h3>
                          <p className="text-sm text-muted-foreground">
                            Brief description of the project and its impact
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {showcaseProjects.length > 6 && (
                <div className="text-center mt-12">
                  <Button variant="outline" onClick={() => router.push("/showcase")}>
                    View All Projects
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-24 px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="p-8 md:p-12 !bg-transparent relative overflow-hidden text-center" style={glassmorphismStyle}>
                <GlassmorphismHighlights />
                <CardContent className="space-y-6">
                  <h2 className="text-4xl md:text-5xl font-bold">
                    Let&apos;s get moving
                  </h2>
                  <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                    Ready to turn your idea into reality? Let&apos;s build something amazing together.
                  </p>
                  <DualCTA />
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Email Modal */}
          <EmailModal isOpen={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)} />
        </>
      ) : (
        /* Step 2: Email Page Content */
        <section className="min-h-[80vh] py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 border-b">
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" onClick={() => setCurrentStep(1)}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                    <div className="text-center">
                      <CardTitle className="text-2xl">Almost there!</CardTitle>
                      <p className="text-muted-foreground mt-1">Just a few more details to get started</p>
                    </div>
                    <div className="w-20"></div> {/* Spacer for alignment */}
                  </div>
                </CardHeader>

                <CardContent className="p-8">
                  {/* Project Summary */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Badge variant="secondary">Your Project</Badge>
                    </h3>
                    {isStealthMode ? (
                      <Alert>
                        <Shield className="h-4 w-4" />
                        <AlertDescription>
                          <span className="font-semibold">Stealth Mode Activated 🥷</span>
                          <br />
                          We&apos;ll discuss your idea privately during our call.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="p-4 bg-muted rounded-lg">
                        <p className="text-lg leading-relaxed">
                          <span className="text-muted-foreground">I want to build a </span>
                          <span className="font-semibold text-primary">{product}</span>
                          <span className="text-muted-foreground"> that will </span>
                          <span className="font-semibold text-primary">{action}</span>
                          <span className="text-muted-foreground"> for </span>
                          <span className="font-semibold text-primary">{customer}</span>
                          <span className="text-muted-foreground">.</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base font-medium">
                        What&apos;s your email address?
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="h-12 text-base"
                        required
                      />
                    </div>

                    {/* Additional Details */}
                    <div className="space-y-2">
                      <Label htmlFor="details" className="text-base font-medium">
                        Additional details (optional)
                      </Label>
                      <Textarea
                        id="details"
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        placeholder="Tell us more about your vision, specific features, or any requirements..."
                        className="min-h-[100px] text-base"
                        rows={4}
                      />
                    </div>

                    {/* File Upload */}
                    <div className="space-y-4">
                      <Label className="text-base font-medium flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Upload files (optional)
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Share mockups, wireframes, or any documents that help explain your idea
                      </p>
                      
                      <FileUploader
                        ref={fileUploaderRef}
                        onUploadComplete={handleFileUploadComplete}
                        onUploadError={handleFileUploadError}
                      />

                      {/* Display uploaded files */}
                      {uploadedFiles.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium">Uploaded Files:</h4>
                          <div className="grid gap-2">
                            {uploadedFiles.map((file, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
                                    <Upload className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{file.filename}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {(file.size / 1024).toFixed(1)} KB
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveFile(index)}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Privacy Policy Agreement */}
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="privacy"
                          checked={agreed}
                          onCheckedChange={(checked) => setAgreed(checked as boolean)}
                        />
                        <Label htmlFor="privacy" className="text-sm leading-relaxed">
                          I agree to the{" "}
                          <Link href="/privacy-policy" className="text-primary hover:underline">
                            Privacy Policy
                          </Link>{" "}
                          and consent to being contacted about this project.
                        </Label>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-12 text-base"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Submit Project Details
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  )
}